import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import getApiBaseUrl from '../utils/api';
import { toDisplayAmount } from '../utils/currency';
import { getLogoForChannelCode } from '../utils/paymentLogos';

const CheckoutPaymentInstructions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [isPolling, setIsPolling] = useState(true);
  const [timeLeft, setTimeLeft] = useState(86400); // 24 hours for virtual accounts
  const [copied, setCopied] = useState({});

  const { paymentData, orderId, paymentMethod } = location.state || {};
  const [vaDetails, setVaDetails] = useState(paymentData?.virtualAccountDetails || null);
  const [amountInfo, setAmountInfo] = useState({ amount: paymentData?.amount, currency: paymentData?.currency });
  const [otcDetails, setOtcDetails] = useState(paymentData?.overTheCounterDetails || null);

  const formatStoreName = (code) => {
    const map = { INDOMARET: 'Indomaret', ALFAMART: 'Alfamart' };
    return map[code] || code;
  };

  const parseExpirySeconds = (iso) => {
    try {
      const end = new Date(iso).getTime();
      const now = Date.now();
      const diff = Math.max(0, Math.floor((end - now) / 1000));
      return diff;
    } catch {
      return null;
    }
  };

  // Redirect if no payment data
  useEffect(() => {
    if (!paymentData || !orderId || !paymentMethod) {
      navigate('/checkout/payment');
    }
  }, [paymentData, orderId, paymentMethod, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0 && paymentStatus === 'pending') {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setPaymentStatus('expired');
      setIsPolling(false);
    }
  }, [timeLeft, paymentStatus]);

  // Poll payment status
  useEffect(() => {
    if (!isPolling || paymentStatus !== 'pending' || !paymentData.xenditPaymentId) return;

    const pollStatus = async () => {
      try {
        const API_BASE_URL = getApiBaseUrl();
          
        const response = await fetch(`${API_BASE_URL}/xendit/payment-status?paymentId=${paymentData.xenditPaymentId}`);
        
        if (response.ok) {
          const data = await response.json();

          // Update VA details if available
          const pm = data.payment?.payment_method;
          const vaNum = pm?.virtual_account?.channel_properties?.virtual_account_number;
          if (vaNum && !vaDetails) {
            setVaDetails({
              account_number: vaNum,
              bank_name: pm?.virtual_account?.channel_code,
              account_holder_name: pm?.virtual_account?.channel_properties?.customer_name,
              amount: data.payment?.amount,
              currency: data.payment?.currency,
              expires_at: pm?.virtual_account?.channel_properties?.expires_at
            });
            const secs = pm?.virtual_account?.channel_properties?.expires_at ? parseExpirySeconds(pm.virtual_account.channel_properties.expires_at) : null;
            if (secs) setTimeLeft(secs);
          }
          // Update OTC details if available
          const otcCode = pm?.over_the_counter?.channel_properties?.payment_code;
          if (pm?.over_the_counter?.channel_code && otcCode && !otcDetails) {
            const details = {
              store: pm.over_the_counter.channel_code,
              payment_code: otcCode,
              amount: data.payment?.amount,
              currency: data.payment?.currency,
              expires_at: pm.over_the_counter.channel_properties?.expires_at,
              customer_name: pm.over_the_counter.channel_properties?.customer_name,
            };
            setOtcDetails(details);
            const secs = details.expires_at ? parseExpirySeconds(details.expires_at) : null;
            if (secs) setTimeLeft(secs);
          }
          if (data.payment?.amount && data.payment?.currency) {
            setAmountInfo({ amount: data.payment.amount, currency: data.payment.currency });
          }

          if (data.status === 'SUCCEEDED') {
            setPaymentStatus('succeeded');
            setIsPolling(false);
            setTimeout(() => {
              navigate(`/checkout/success?order_id=${orderId}&payment_id=${paymentData.xenditPaymentId}`, { 
                state: { 
                  orderId, 
                  paymentId: paymentData.xenditPaymentId,
                  paymentMethod: paymentMethod.name 
                }
              });
            }, 2000);
          } else if (data.status === 'FAILED' || data.status === 'EXPIRED') {
            setPaymentStatus('failed');
            setIsPolling(false);
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    const interval = setInterval(pollStatus, 10000); // Poll every 10 seconds for virtual accounts
    return () => clearInterval(interval);
  }, [isPolling, paymentStatus, paymentData.xenditPaymentId, orderId, navigate, paymentMethod.name, vaDetails, otcDetails]);

  // Format time display
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Copy to clipboard function
  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [key]: true });
      setTimeout(() => {
        setCopied({ ...copied, [key]: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  // Handle manual refresh
  const handleRefreshStatus = async () => {
    try {
      const API_BASE_URL = getApiBaseUrl();
        
      const response = await fetch(`${API_BASE_URL}/xendit/payment-status?paymentId=${paymentData.xenditPaymentId}`);
      
      if (response.ok) {
        const data = await response.json();

        const pm = data.payment?.payment_method;
        const vaNum = pm?.virtual_account?.channel_properties?.virtual_account_number;
        if (vaNum && !vaDetails) {
          setVaDetails({
            account_number: vaNum,
            bank_name: pm?.virtual_account?.channel_code,
            account_holder_name: pm?.virtual_account?.channel_properties?.customer_name,
            amount: data.payment?.amount,
            currency: data.payment?.currency,
            expires_at: pm?.virtual_account?.channel_properties?.expires_at
          });
          const secs = pm?.virtual_account?.channel_properties?.expires_at ? parseExpirySeconds(pm.virtual_account.channel_properties.expires_at) : null;
          if (secs) setTimeLeft(secs);
        }
        const otcCode = pm?.over_the_counter?.channel_properties?.payment_code;
        if (pm?.over_the_counter?.channel_code && otcCode && !otcDetails) {
          const details = {
            store: pm.over_the_counter.channel_code,
            payment_code: otcCode,
            amount: data.payment?.amount,
            currency: data.payment?.currency,
            expires_at: pm.over_the_counter.channel_properties?.expires_at,
            customer_name: pm.over_the_counter.channel_properties?.customer_name,
          };
          setOtcDetails(details);
          const secs = details.expires_at ? parseExpirySeconds(details.expires_at) : null;
          if (secs) setTimeLeft(secs);
        }
        if (data.payment?.amount && data.payment?.currency) {
          setAmountInfo({ amount: data.payment.amount, currency: data.payment.currency });
        }

        if (data.status === 'SUCCEEDED') {
          setPaymentStatus('succeeded');
          navigate(`/checkout/success?order_id=${orderId}&payment_id=${paymentData.xenditPaymentId}`, { 
            state: { 
              orderId, 
              paymentId: paymentData.xenditPaymentId,
              paymentMethod: paymentMethod.name 
            }
          });
        } else if (data.status === 'FAILED' || data.status === 'EXPIRED') {
          setPaymentStatus('failed');
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  // Render copy button
  const CopyButton = ({ text, copyKey, label }) => (
    <button
      onClick={() => copyToClipboard(text, copyKey)}
      className="ml-2 px-3 py-1 bg-primary text-dark text-xs rounded hover:bg-primary/80 transition-colors"
      title={`Copy ${label}`}
    >
      {copied[copyKey] ? 'Copied!' : 'Copy'}
    </button>
  );

  if (!paymentData) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">Payment Instructions Not Available</h1>
          <p className="text-light mb-8">Please try again from the payment page.</p>
          <Button onClick={() => navigate('/checkout/payment')} variant="primary">
            Back to Payment
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6 text-center">
            Complete Your Payment
          </h1>

          {paymentStatus === 'pending' && (
            <div className="mb-6">
              <div className="bg-blue-900/20 text-blue-400 p-4 rounded-lg mb-4 text-center">
                <p className="font-semibold mb-2">Payment Pending</p>
                <p className="text-sm">Follow the instructions below to complete your payment</p>
              </div>
              <div className="text-lg font-mono text-primary mb-2 text-center">
                Time remaining: {formatTime(timeLeft)}
              </div>
            </div>
          )}

          {paymentStatus === 'succeeded' && (
            <div className="bg-green-900/20 text-green-400 p-4 rounded-lg mb-6 text-center">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-semibold mb-2">Payment Received!</p>
              <p className="text-sm">Redirecting to confirmation page...</p>
            </div>
          )}

          {paymentStatus === 'failed' && (
            <div className="bg-red-900/20 text-red-400 p-4 rounded-lg mb-6 text-center">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-semibold mb-2">Payment Failed</p>
              <p className="text-sm">The payment could not be completed</p>
            </div>
          )}

          {paymentStatus === 'expired' && (
            <div className="bg-yellow-900/20 text-yellow-400 p-4 rounded-lg mb-6 text-center">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-semibold mb-2">Payment Expired</p>
              <p className="text-sm">Please create a new payment request</p>
            </div>
          )}

          {/* Payment Method Information */}
          <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-6">
            <h2 className="font-bold text-light mb-4 text-center">
              {paymentMethod.name} Payment Instructions
            </h2>
            
            {/* Virtual Account Details */}
          {vaDetails && (
            <div className="space-y-4">
              {/* Highlight box for account/QR/payment code */}
              <div className="bg-primary/10 border border-primary/40 rounded-md p-4 text-center">
                <div className="text-gray-300 text-sm mb-1">Virtual Account Number</div>
                <div className="flex items-center justify-center gap-2">
                  <div className="text-2xl font-mono text-primary tracking-wider">{vaDetails.account_number}</div>
                  <CopyButton text={vaDetails.account_number} copyKey="vaNumberTop" label="Virtual Account Number" />
                </div>
                {vaDetails.expires_at && (
                  <div className="text-gray-400 text-xs mt-1">Valid until: {new Date(vaDetails.expires_at).toLocaleString()}</div>
                )}
              </div>
              <div className="bg-dark border border-gray-700 rounded-md p-4">
                <h3 className="font-bold text-light mb-3">Bank Transfer Details:</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Bank:</span>
                      <div className="flex items-center">
                      <span className="text-light font-mono flex items-center gap-2">
                        {vaDetails.bank_name && (
                          <img src={getLogoForChannelCode(vaDetails.bank_name)} alt={vaDetails.bank_name} className="w-6 h-6 rounded-sm object-contain bg-white/5" />
                        )}
                        <span>{vaDetails.bank_name || paymentMethod.name}</span>
                      </span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Account Number:</span>
                      <div className="flex items-center">
                        <span className="text-light font-mono">
                          {vaDetails.account_number}
                        </span>
                        <CopyButton 
                          text={vaDetails.account_number} 
                          copyKey="accountNumber" 
                          label="Account Number"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Amount:</span>
                      <div className="flex items-center">
                        <span className="text-light font-mono text-lg">
                          {new Intl.NumberFormat((amountInfo.currency || 'USD') === 'IDR' ? 'id-ID' : 'en-US', { style: 'currency', currency: amountInfo.currency || 'USD' }).format(toDisplayAmount(amountInfo.amount || vaDetails.amount, amountInfo.currency || vaDetails.currency))}
                        </span>
                        <CopyButton 
                          text={(amountInfo.amount || vaDetails.amount).toString()} 
                          copyKey="amount" 
                          label="Amount"
                        />
                      </div>
                    </div>
                    
                    {/* Intentionally not displaying payer's name to avoid exposing personal data */}
                  </div>
                </div>
              </div>
            )}

            {/* Over The Counter Details (e.g., Indomaret) */}
            {otcDetails && (
              <div className="space-y-4">
                <div className="bg-primary/10 border border-primary/40 rounded-md p-4 text-center">
                  <div className="text-gray-300 text-sm mb-1">Payment Code</div>
                  <div className="flex items-center justify-center gap-2">
                    <div className="text-2xl font-mono text-primary tracking-wider">{otcDetails.payment_code}</div>
                    <CopyButton text={otcDetails.payment_code} copyKey="paymentCodeTop" label="Payment Code" />
                  </div>
                  {otcDetails.expires_at && (
                    <div className="text-gray-400 text-xs mt-1">Valid until: {new Date(otcDetails.expires_at).toLocaleString()}</div>
                  )}
                </div>
                <div className="bg-dark border border-gray-700 rounded-md p-4">
                  <h3 className="font-bold text-light mb-3">Store Payment Details:</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Store:</span>
                      <span className="text-light font-mono flex items-center gap-2">
                        <img src={getLogoForChannelCode(otcDetails.store)} alt={formatStoreName(otcDetails.store)} className="w-8 h-5 rounded-sm object-contain bg-white/5" />
                        <span>{formatStoreName(otcDetails.store)}</span>
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Payment Code:</span>
                      <div className="flex items-center">
                        <span className="text-light font-mono text-lg">{otcDetails.payment_code}</span>
                        <CopyButton text={otcDetails.payment_code} copyKey="paymentCode" label="Payment Code" />
                      </div>
                    </div>
                    {otcDetails.expires_at && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Valid Until:</span>
                        <span className="text-light">
                          {new Date(otcDetails.expires_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Amount:</span>
                      <div className="flex items-center">
                        <span className="text-light font-mono text-lg">
                          {new Intl.NumberFormat((amountInfo.currency || 'USD') === 'IDR' ? 'id-ID' : 'en-US', { style: 'currency', currency: amountInfo.currency || 'USD' }).format(toDisplayAmount(amountInfo.amount || otcDetails.amount, amountInfo.currency || otcDetails.currency))}
                        </span>
                        <CopyButton text={(amountInfo.amount || otcDetails.amount).toString()} copyKey="amount" label="Amount" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Generic Payment Instructions */}
            {/* Generic info only if no specific method details exist */}
            {!vaDetails && !otcDetails && (
              <div className="bg-dark border border-gray-700 rounded-md p-4">
                <h3 className="font-bold text-light mb-3">Payment Information:</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Payment Method:</span>
                    <span className="text-light">{paymentMethod.name}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Amount:</span>
                    <div className="flex items-center">
                      <span className="text-light font-mono text-lg">
                        {new Intl.NumberFormat((amountInfo.currency || 'USD') === 'IDR' ? 'id-ID' : 'en-US', { style: 'currency', currency: amountInfo.currency || 'USD' }).format(toDisplayAmount(amountInfo.amount, amountInfo.currency))}
                      </span>
                      <CopyButton 
                        text={(amountInfo.amount || 0).toString()} 
                        copyKey="amount" 
                        label="Amount"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Order ID:</span>
                    <div className="flex items-center">
                      <span className="text-light font-mono">{orderId}</span>
                      <CopyButton text={orderId} copyKey="orderId" label="Order ID" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="mt-6">
              <h3 className="font-bold text-light mb-4">How to Complete Payment:</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-400 text-sm">
                {vaDetails ? (
                  <>
                    <li>Go to your mobile banking app or visit an ATM</li>
                    <li>Select "Transfer" or "Payment" option</li>
                    <li>Enter the bank and account number shown above</li>
                    <li>Transfer the exact amount: {new Intl.NumberFormat((amountInfo.currency || 'USD') === 'IDR' ? 'id-ID' : 'en-US', { style: 'currency', currency: amountInfo.currency || 'USD' }).format(toDisplayAmount(amountInfo.amount || vaDetails?.amount, amountInfo.currency || vaDetails?.currency))}</li>
                    <li>Keep your transaction receipt for reference</li>
                    <li>Payment will be confirmed automatically (this page will update)</li>
                  </>
                ) : otcDetails ? (
                  <>
                    <li>Visit the nearest {formatStoreName(otcDetails.store)} store</li>
                    <li>Tell the cashier you want to pay using payment code</li>
                    <li>Provide this payment code: <span className="font-mono text-light">{otcDetails.payment_code}</span></li>
                    <li>Pay the exact amount shown above</li>
                    <li>Keep your receipt. This page updates automatically after payment</li>
                  </>
                ) : (
                  <>
                    <li>Open your {paymentMethod.name} app or website</li>
                    <li>Look for payment or transfer options</li>
                    <li>Use the Order ID as your reference number</li>
                    <li>Pay the exact amount shown above</li>
                    <li>Complete the payment process</li>
                    <li>Wait for payment confirmation (this page will update automatically)</li>
                  </>
                )}
              </ol>
            </div>

            {/* Important Notes */}
            <div className="mt-6 bg-yellow-900/20 text-yellow-400 p-4 rounded-md">
              <h4 className="font-bold mb-2">⚠️ Important Notes:</h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Pay the exact amount - incorrect amounts may cause delays</li>
                <li>Payment must be completed within the time limit</li>
                <li>Keep your transaction receipt until order is confirmed</li>
                <li>This page will automatically update when payment is received</li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-4 mb-6">
            {paymentStatus === 'pending' && (
              <>
                <Button 
                  variant="secondary" 
                  onClick={handleRefreshStatus}
                >
                  Check Payment Status
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/checkout/payment')}
                >
                  Try Different Method
                </Button>
              </>
            )}

            {(paymentStatus === 'failed' || paymentStatus === 'expired') && (
              <>
                <Button 
                  variant="primary" 
                  onClick={() => navigate('/checkout/payment')}
                >
                  Try Again
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => navigate('/products')}
                >
                  Back to Products
                </Button>
              </>
            )}
          </div>

          {/* Order Information */}
          {!otcDetails && !vaDetails && (
            <div className="mt-8 pt-8 border-t border-gray-800 text-center">
              <h3 className="font-bold text-light mb-2">Order Information:</h3>
              <div className="space-y-1 text-gray-400 text-sm">
                <p>Order ID: {orderId}</p>
                {paymentData.xenditPaymentId && (
                  <p>Payment ID: {paymentData.xenditPaymentId}</p>
                )}
                <p>Payment Method: {paymentMethod.name}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutPaymentInstructions;
